import { addParamToURL, openWithTenant } from '@app-store'
import { TabInterface, Tabs } from '@ui-kit'
import { useCallback, useEffect, useMemo, useState } from 'react'
import EntityLayoutCasePane from '../../../components/workflow/EntityLayoutTask'
import { useAppDispatch } from '../../../utils/agent.store'
import { loadCaseWorkflow } from './loadCaseWorkflow'
import { FieldValues } from 'react-hook-form'
import { editEntityDataEmbeddedAPI } from '../../../apis/entityData.api'
import {
  ENTITY_NAME,
  RECORD_STATE,
  WORKFLOW_MODAL_TYPES,
} from '../../../utils/constants'
import { MultiProductModalType } from '../../../utils/types'
import { MultiProductPopupModal } from './MultiProductPopupModal'
import { CaseTabsReviewPaneProps } from '../../../types/multiProduct.types'

export const CaseTabsReviewPane = ({
  childCases,
  faxDetails,
  intakeEntityName,
  updateStatusToCompleted,
  updateStatusToProcessing,
  refetchEnrollment,
}: CaseTabsReviewPaneProps) => {
  const dispatch = useAppDispatch()
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [activeModal, setActiveModal] = useState<MultiProductModalType>(null)
  const [papReviewData, setPapReviewData] = useState<{
    url?: string
  }>({})
  const [loadedTabs, setLoadedTabs] = useState<Record<number, boolean>>({})
  const [defaultActiveTab, setDefaultActiveTab] = useState<TabInterface>()

  const activeCase = childCases[activeTabIndex]

  const loadTabWorkflow = useCallback(
    async (caseId: string, tabIndex: number) => {
      setLoadedTabs((prev) => ({ ...prev, [tabIndex]: false }))
      addParamToURL('caseId', caseId)
      await loadCaseWorkflow(dispatch, caseId, { useIntakeLayout: true })
      setLoadedTabs((prev) => ({ ...prev, [tabIndex]: true }))
    },
    [dispatch]
  )

  useEffect(() => {
    const caseId = activeCase?.case_id ?? activeCase?.case?.id
    if (caseId) {
      loadTabWorkflow(String(caseId), activeTabIndex)
    }
  }, [
    activeCase?.case_id,
    activeCase?.case?.id,
    activeTabIndex,
    loadTabWorkflow,
  ])

  const updateCaseWorkflowProceededVariableToTrue = async (caseId?: string) => {
    const targetCaseId = caseId || defaultActiveTab?.key?.toString()
    const enrollmentDetailsRecord = childCases?.find(
      (item: FieldValues) =>
        item?.case_id?.toString() === targetCaseId ||
        item?.id?.toString() === targetCaseId
    )?.id

    const data = {
      workflow_proceeded: true,
      _state: RECORD_STATE.CHANGED,
    }

    await editEntityDataEmbeddedAPI(
      ENTITY_NAME.HCP_ENROLLMENT_DETAILS,
      enrollmentDetailsRecord ?? '',
      data,
      ['communication_cases', 'programs']
    )

    //if (it is the last case then update status to completed)
    if ((tabs?.length ?? 0) <= 1) {
      await updateStatusToCompleted?.()
    }
  }

  const tabs: TabInterface[] = useMemo(
    () =>
      childCases
        ?.filter((childCase) => !childCase?.workflow_proceeded)
        ?.map((c, index) => ({
          label: c.case?.case_key ?? `${ENTITY_NAME.CASE} ${c.case_id ?? c.id}`,
          key: String(c.case_id ?? c.id),
          component:
            loadedTabs[index] && index === activeTabIndex ? (
              <EntityLayoutCasePane
                key={activeTabIndex}
                faxDetails={faxDetails}
                intakeEntityName={intakeEntityName}
                updateStatusToCompleted={updateStatusToCompleted}
                updateStatusToProcessing={updateStatusToProcessing}
                onMultiProductCaseComplete={async () =>
                  await updateCaseWorkflowProceededVariableToTrue(
                    c.case_id?.toString() ?? ''
                  )
                }
                showSaveAsDraft={false}
                setPapReviewModal={async (url) => {
                  setPapReviewData({ url })
                  url && openWithTenant(url, '_blank')
                  setActiveModal(null)
                  setLoadedTabs({})
                  setDefaultActiveTab(undefined)
                  setActiveTabIndex(0)
                  await refetchEnrollment?.()
                }}
                refetchEnrollment={refetchEnrollment}
              />
            ) : null,
        })),
    [
      childCases,
      loadedTabs,
      activeTabIndex,
      faxDetails,
      intakeEntityName,
      updateStatusToCompleted,
      updateStatusToProcessing,
    ]
  )

  useEffect(() => {
    if (tabs?.length) {
      const isCurrentTabInTabs = tabs.some(
        (tab) => tab.key === defaultActiveTab?.key
      )

      if (!defaultActiveTab || !isCurrentTabInTabs) {
        setDefaultActiveTab(tabs[0])
        setActiveTabIndex(0)
      }
    }
  }, [tabs, childCases])

  return (
    <div className="flex flex-col h-full max-h-screen pt-4 pr-2 border border-1 intakeQueReview">
      {activeModal && (
        <MultiProductPopupModal
          activeModal={activeModal}
          closeModal={() => setActiveModal(null)}
          onConfirmClick={async () => {
            if (
              activeModal === WORKFLOW_MODAL_TYPES.INTAKE_COMPLETE &&
              papReviewData?.url
            ) {
              openWithTenant(papReviewData?.url, '_blank')
              setActiveModal(null)
              setLoadedTabs({})
              setDefaultActiveTab(undefined)
              setActiveTabIndex(0)
              await refetchEnrollment?.()
            }
          }}
        />
      )}
      <div className="flex-1 min-h-0 px-3 pt-2">
        <Tabs
          tabs={tabs}
          defaultActive={defaultActiveTab}
          tabsWrapperClassName="h-full flex flex-col"
          tabsContentClassName="flex-1 min-h-0 min-w-0 overflow-auto"
          fontWeight="font-medium"
          onChange={(_label, index) => {
            setActiveTabIndex(index)
            setDefaultActiveTab(tabs[index])
          }}
        ></Tabs>
      </div>
    </div>
  )
}

export default CaseTabsReviewPane
